import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { statisticsApi } from '../api/statistics'
import type { ResourceStatsTimeSeriesResponse, ResourceMetric, Granularity } from '../types/statistics'

interface UseResourceStatisticsOptions {
  enabled?: boolean
  refresh?: boolean // Force backend cache bypass
}

export const useResourceStatistics = (
  resourceId: string | undefined,
  startDate: string,
  endDate: string,
  granularity: Granularity,
  options?: UseResourceStatisticsOptions
): UseQueryResult<ResourceStatsTimeSeriesResponse, Error> => {
  return useQuery({
    queryKey: ['resource-statistics', resourceId, startDate, endDate, granularity, options?.refresh],
    queryFn: () => {
      if (!resourceId) throw new Error('Resource ID is required')
      return statisticsApi.getResourceTimeSeries(resourceId, startDate, endDate, granularity, options?.refresh)
    },
    enabled: options?.enabled && !!resourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes - balance freshness with performance
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache (replaces deprecated cacheTime)
    refetchOnWindowFocus: false, // Don't auto-refetch on window focus
    refetchOnMount: false, // Don't auto-refetch on component mount
  })
}

export const useResourceLatestMetrics = (
  resourceId: string | undefined,
  days: number = 7,
  options?: UseResourceStatisticsOptions
): UseQueryResult<ResourceMetric[], Error> => {
  return useQuery({
    queryKey: ['resource-latest-metrics', resourceId, days],
    queryFn: () => {
      if (!resourceId) throw new Error('Resource ID is required')
      return statisticsApi.getResourceLatestMetrics(resourceId, days)
    },
    enabled: options?.enabled && !!resourceId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}
